import React from 'react';
import { User, Bot, Users, Cake } from 'lucide-react';

function getUpcomingBirthday(relative) {
  const bd = relative.birthDate;
  if (!bd?.birthMonth || !bd?.birthDay) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let next = new Date(today.getFullYear(), bd.birthMonth - 1, bd.birthDay);
  if (next < today) next = new Date(today.getFullYear() + 1, bd.birthMonth - 1, bd.birthDay);
  const daysUntil = Math.round((next - today) / (1000 * 60 * 60 * 24));
  if (daysUntil > 7) return null;
  return daysUntil;
}

function RelativesList({ relatives, families = [], onSelectRelative, onOpenChat, onSelectFamily }) {
  const getInitial = (name) => name ? name[0].toUpperCase() : '?';

  const getProfileImageUrl = (relative) => {
    return relative.profile?.profilePictureUrl || relative.profilePictureUrl || null;
  };

  const upcomingBirthdays = relatives
    .map(r => ({ relative: r, daysUntil: getUpcomingBirthday(r) }))
    .filter(b => b.daysUntil !== null)
    .sort((a, b) => a.daysUntil - b.daysUntil);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      {/* Upcoming Birthdays */}
      {upcomingBirthdays.length > 0 && (
        <>
          <h2 className="text-lg font-bold mb-3 px-1">Upcoming Birthdays</h2>
          <div className="space-y-2 mb-8">
            {upcomingBirthdays.map(({ relative, daysUntil }) => {
              const imageUrl = getProfileImageUrl(relative);
              const label = daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`;
              return (
                <div
                  key={`bday-${relative.uuid}`}
                  onClick={() => onSelectRelative(relative)}
                  className="flex items-center gap-4 bg-gradient-to-r from-purple-900/40 to-gray-800 rounded-2xl p-4 cursor-pointer hover:from-purple-900/60 transition-colors"
                >
                  <div className="relative">
                    {imageUrl ? (
                      <img src={imageUrl} alt={relative.name} className="w-14 h-14 rounded-full object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gray-700 flex items-center justify-center">
                        <span className="text-xl font-semibold text-gray-400">{getInitial(relative.name)}</span>
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                      <Cake size={12} className="text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-white">{relative.name}</p>
                    <p className="text-sm text-purple-300">{label}</p>
                  </div>
                  {daysUntil === 0 && (
                    <span className="text-2xl">🎂</span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Family Members */}
      <h2 className="text-lg font-bold mb-3 px-1">Family Members</h2>

      {relatives.length === 0 ? (
        <div className="text-center py-12">
          <User className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No family members found</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
          {relatives.map((relative) => {
            const imageUrl = getProfileImageUrl(relative);
            return (
              <div
                key={relative.uuid}
                onClick={() => onSelectRelative(relative)}
                className="relative aspect-square rounded-3xl overflow-hidden cursor-pointer hover:scale-[1.03] transition-transform duration-200 bg-gray-800"
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={relative.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-700">
                    <span className="text-4xl font-semibold text-gray-400">
                      {getInitial(relative.name)}
                    </span>
                  </div>
                )}

                {/* Name overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8">
                  <p className="font-semibold text-white text-sm drop-shadow-lg">{relative.name}</p>
                  <p className="text-xs text-gray-300 drop-shadow">{relative.relationship}</p>
                </div>

                {/* Birthday badge */}
                {getUpcomingBirthday(relative) !== null && (
                  <div className="absolute top-2 right-2 w-8 h-8 bg-purple-600/80 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Cake size={14} className="text-white" />
                  </div>
                )}

                {/* Memorial indicator */}
                {relative.isMemorial && (
                  <div className="absolute top-2 left-2 bg-amber-600/80 px-2 py-0.5 rounded-full text-[10px] font-medium">
                    In Memoriam
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Selfie Mode */}
      {relatives.length > 0 && (
        <>
          <h2 className="text-lg font-bold mb-3 px-1">Selfie Mode</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
            {relatives.map((relative) => {
              const imageUrl = getProfileImageUrl(relative);
              return (
                <div
                  key={`chat-${relative.uuid}`}
                  onClick={() => onOpenChat(relative)}
                  className="relative aspect-square rounded-3xl overflow-hidden cursor-pointer hover:scale-[1.03] transition-transform duration-200 bg-gray-800"
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={relative.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-700">
                      <span className="text-4xl font-semibold text-gray-400">
                        {getInitial(relative.name)}
                      </span>
                    </div>
                  )}

                  {/* Purple overlay */}
                  <div className="absolute inset-0 bg-purple-600/15" />

                  {/* Bot badge */}
                  <div className="absolute top-2 right-2 w-9 h-9 bg-gray-900/60 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Bot size={18} className="text-purple-400" />
                  </div>

                  {/* Name overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8">
                    <p className="font-semibold text-white text-sm drop-shadow-lg">{relative.name}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Family Groups */}
      {families.length > 0 && (
        <>
          <h2 className="text-lg font-bold mb-3 px-1">Family Groups</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {families.map((family) => (
              <div
                key={family.uuid}
                onClick={() => onSelectFamily(family)}
                className="relative aspect-square rounded-3xl overflow-hidden bg-gradient-to-br from-purple-900/40 to-gray-800 p-4 flex flex-col items-center justify-center cursor-pointer hover:scale-[1.03] transition-transform duration-200"
              >
                {/* Overlapping member avatars */}
                <div className="flex items-center justify-center mb-3">
                  {(() => {
                    const familyRelatives = relatives.filter(r =>
                      (r.families || []).some(f => f.uuid === family.uuid)
                    );
                    if (familyRelatives.length === 0) {
                      return <Users size={32} className="text-gray-500" />;
                    }
                    return familyRelatives.slice(0, 4).map((rel) => {
                      const memberImage = getProfileImageUrl(rel);
                      return (
                        <div
                          key={rel.uuid}
                          className="w-14 h-14 rounded-full bg-gray-600 border-2 border-gray-800 overflow-hidden flex items-center justify-center -ml-3 first:ml-0"
                        >
                          {memberImage ? (
                            <img src={memberImage} alt={rel.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-sm font-semibold text-gray-300">
                              {getInitial(rel.name)}
                            </span>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>

                <p className="font-semibold text-white text-center text-sm">{family.name}</p>
                {family.memberCount && (
                  <p className="text-xs text-gray-400 mt-1">{family.memberCount} members</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default RelativesList;
